"use client";
import { useEffect, useState } from "react";
import { type ClientPackageType, type Translationtype } from "utils";
import { getClientPackages } from "../services";
import { Confirm } from "./confirm";
import { toastMessage } from "./toast";

interface PackageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (blobName: string) => void;
  t: Translationtype;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(2)} KiB`;
  }
  return `${bytes} B`;
}

export function PackageSelectorModal({
  isOpen,
  onClose,
  onSelect,
  t,
}: PackageSelectorModalProps): JSX.Element {
  const [packages, setPackages] = useState<ClientPackageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBlobName, setSelectedBlobName] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedBlobName(null);
      setLoading(true);
      getClientPackages()
        .then((data) => setPackages(data))
        .catch((err) => {
          console.error("Failed to fetch client packages:", err);
          toastMessage.error(t("Devices.clientPackagesFetchError"));
          setPackages([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedBlobName) {
      onSelect(selectedBlobName);
    }
  };

  return (
    <Confirm
      title={t("Devices.selectTargetPackage")}
      isOpened={isOpen}
      handleOk={handleConfirm}
      handleClose={onClose}
      okBtnText={t("Buttons.confirm")}
      cancelBtnText={t("Buttons.cancel")}
      okBtnClassName="btn btn-icon"
      hideOkBtn={!selectedBlobName}
      t={t}
    >
      {loading ? (
        <div className="flex justify-center py-6">
          <span className="loading loading-spinner" />
        </div>
      ) : packages.length === 0 ? (
        <p className="text-center py-4 text-gray-500">
          {t("Devices.noPackagesAvailable")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th></th>
                <th>{t("Devices.packageName")}</th>
                <th>{t("Devices.packageSize")}</th>
                <th>{t("Devices.packageLastModified")}</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr
                  key={pkg.blobName}
                  className="cursor-pointer hover"
                  onClick={() => setSelectedBlobName(pkg.blobName)}
                >
                  <td>
                    <input
                      type="radio"
                      name="selectedPackage"
                      checked={selectedBlobName === pkg.blobName}
                      onChange={() => setSelectedBlobName(pkg.blobName)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="font-mono text-sm">{pkg.blobName}</td>
                  <td>{formatBytes(pkg.sizeBytes)}</td>
                  <td>
                    {new Date(pkg.lastModified).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Confirm>
  );
}
